import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateUsersReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";

const UsersReport = ({
    reportType,
    filters,
    onFilterUpdate,
    onFilterReset,
    onExport,
    onRefresh,
    onReportDataUpdate,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();

    const { data: reportData, isLoading, error, refetch } = useGenerateUsersReportQuery(localFilters);

    // Update parent with report data whenever it changes
    useEffect(() => {
        if (reportData && onReportDataUpdate) {
            onReportDataUpdate("users", reportData);
        }
    }, [reportData, onReportDataUpdate]);

    const handleFilterChange = (field, value) => {
        // Extract values from MultipleSelectJson objects
        let processedValue = value;
        if (field.includes("_ids") && Array.isArray(value)) {
            processedValue = value.map((item) => item.value || item);
            // Remove duplicates
            processedValue = [...new Set(processedValue)];
        }

        const newFilters = { ...localFilters, [field]: processedValue };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    const handleReset = () => {
        setLocalFilters({
            start_date: null,
            end_date: null,
            role: null,
            region_ids: [],
            is_active: null,
            is_b2b: null,
            format: "json",
        });
        onFilterReset();
    };

    const getRoleColor = (role) => {
        const colors = {
            admin: "error",
            distributor: "info",
            staff: "success",
        };
        return colors[role] || "default";
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Role"
                            value={localFilters.role || ""}
                            onChange={(e) => handleFilterChange("role", e.target.value)}
                            name="role"
                            options={[
                                { value: "", label: "All Roles" },
                                { value: "admin", label: "Admin" },
                                { value: "distributor", label: "Distributor" },
                                { value: "staff", label: "Staff" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Status"
                            value={localFilters.is_active === null ? "" : localFilters.is_active}
                            onChange={(e) =>
                                handleFilterChange("is_active", e.target.value === "" ? null : e.target.value)
                            }
                            name="is_active"
                            options={[
                                { value: "", label: "All Status" },
                                { value: true, label: "Active" },
                                { value: false, label: "Inactive" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                label="Regions"
                                value={localFilters.region_ids || []}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="B2B Status"
                            value={localFilters.is_b2b === null ? "" : localFilters.is_b2b}
                            onChange={(e) =>
                                handleFilterChange("is_b2b", e.target.value === "" ? null : e.target.value)
                            }
                            name="is_b2b"
                            options={[
                                { value: "", label: "All B2B Status" },
                                { value: true, label: "B2B" },
                                { value: false, label: "B2C" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate users report"}
                </Alert>
            )}

            {reportData && !isLoading && (
                <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.total_users || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Active Users
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.active_users || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        B2B Users
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.b2b_users || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Role Breakdown */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Role Breakdown
                                </Typography>
                                {reportData.summary?.role_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.role_breakdown).map(([role, count]) => (
                                            <Box key={role} display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2" textTransform="capitalize">
                                                    {role}
                                                </Typography>
                                                <Chip label={count} size="small" color={getRoleColor(role)} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Users Table */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Users Details ({reportData.users?.length || 0} users)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Mobile</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Region</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>B2B</TableCell>
                                        <TableCell>Created Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.users?.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.mobile}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip label={user.role} size="small" color={getRoleColor(user.role)} />
                                            </TableCell>
                                            <TableCell>{user.region?.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.is_active ? "Active" : "Inactive"}
                                                    size="small"
                                                    color={user.is_active ? "success" : "error"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.is_b2b ? "Yes" : "No"}
                                                    size="small"
                                                    color={user.is_b2b ? "info" : "default"}
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {!reportData && !isLoading && !error && (
                <Alert severity="info">No data found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default UsersReport;
